import {
  formatDisplayGrade,
  formatDisplayPoints,
  getToolIds,
  precisionFrom,
  round,
  GRADEBOOK_SETTINGS,
} from '@macmillan-learning/gradebook-shared';

/**
 * Format a grade for display (percent mode)
 * Uses the shared utility to ensure consistent formatting with V1
 *
 * @param {number|null} grade - Raw grade value (0-1 decimal)
 * @param {Object} assignment - Assignment/grade data object
 * @param {Array} settings - Gradebook settings array
 * @param {number} roundPercentType - Rounding precision setting
 * @returns {string} Formatted grade string
 */
export function formatGradePercent(
  grade,
  assignment,
  settings,
  roundPercentType,
) {
  if (grade === null || grade === undefined) return '--';

  return formatDisplayGrade(
    grade,
    assignment,
    settings,
    { isAssigned: assignment?.isAssigned ?? true },
    roundPercentType,
  );
}

/**
 * Format points for display (points mode)
 *
 * Student points always display with 2 decimal places.
 * This is NOT affected by the roundPercentType setting which only affects percentages.
 *
 * @param {Object} grade - Grade data object with studentPoints, assigned_points, tool, etc.
 * @param {Array} settings - Gradebook settings array
 * @returns {string} Formatted points string
 */
export function formatGradePoints(grade, settings) {
  if (
    !grade ||
    grade.studentPoints === null ||
    grade.studentPoints === undefined
  ) {
    return '--';
  }

  // Student points always show 2 decimal places
  const precision = 2;

  return formatDisplayPoints(grade, settings, {
    isAssigned: grade?.isAssigned ?? true,
    precision,
    ignoreDecimalZeros: false,
  });
}

/**
 * Check if a number has decimal places
 * @param {number} value - The number to check
 * @returns {boolean} True if the number has decimal places
 */
export function getHasDecimalPoints(value) {
  return !isNaN(value) && !Number.isInteger(value);
}

/**
 * Determine if decimals should be shown in points display
 *
 * @param {Object} grade - Grade object with tool and assigned_points/pointsPossible
 * @returns {boolean} True if decimals should be shown
 */
export function shouldShowPointDecimals(grade) {
  if (!grade) return false;

  const { reef } = getToolIds();
  const isReefAssignment = grade.tool === reef;
  // Check both assigned_points and pointsPossible for compatibility with different data shapes
  const points = grade.assigned_points ?? grade.pointsPossible;
  const hasDecimalPoints = getHasDecimalPoints(points);

  return isReefAssignment || hasDecimalPoints;
}

/**
 * Get "Out of X" label for points display
 *
 * @param {number} assignedPoints - Total possible points
 * @param {boolean} showDecimals - Whether to show decimal places
 * @returns {string|null} "Out of X" string or null
 */
export function getOutOfLabel(assignedPoints, showDecimals = false) {
  if (!assignedPoints && assignedPoints !== 0) return null;
  const precision = showDecimals ? 2 : 0;
  return `Out of ${round(parseFloat(assignedPoints), precision, !showDecimals)}`;
}

/**
 * Legacy formatGrade function for backward compatibility
 * Now delegates to the proper shared utilities
 *
 * @param {number|null} score - Grade score (can be 0-1 decimal or 0-100 percentage depending on context)
 * @param {Object} options - Formatting options
 * @param {boolean} options.showPercent - Whether to show as percentage
 * @param {number} options.pointsEarned - Points earned (for points mode)
 * @param {number} options.pointsPossible - Points possible (for points mode)
 * @param {Object} options.grade - Full grade object with raw data
 * @param {Array} options.settings - Gradebook settings
 * @param {number} options.roundPercentType - Rounding precision setting
 * @returns {string} Formatted grade string
 */
export function formatGrade(score, options = {}) {
  const {
    showPercent = true,
    pointsEarned,
    pointsPossible,
    grade,
    settings = [],
    roundPercentType = GRADEBOOK_SETTINGS.roundPercentWhole,
  } = options;

  if (score === null || score === undefined) return '--';

  // If we have a full grade object with raw data, use shared utilities
  if (grade && grade.studentGrade !== undefined) {
    if (showPercent) {
      return formatGradePercent(
        grade.studentGrade,
        grade,
        settings,
        roundPercentType,
      );
    } else {
      return formatGradePoints(grade, settings);
    }
  }

  // Fallback for legacy usage (score is already 0-100 percentage)
  if (showPercent) {
    const precision = precisionFrom(roundPercentType);
    const formattedScore = round(parseFloat(score), precision);
    return `${formattedScore}%`;
  }

  // Show points if available
  if (pointsEarned !== undefined && pointsPossible !== undefined) {
    return round(parseFloat(pointsEarned), 2, true);
  }

  // Final fallback
  const precision = precisionFrom(roundPercentType);
  return `${round(parseFloat(score), precision)}%`;
}

export function formatDate(date) {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Check if a grade has a due date exception for the student
 * @param {Object} grade - Grade object with exceptions array and studentId
 * @returns {boolean} True if has due date exception
 */
export function hasDueDateException(grade) {
  if (!grade?.exceptions?.length || !grade?.studentId) return false;

  return grade.exceptions.some(
    (exception) =>
      exception.exception_type === 'due_date' &&
      exception.user_ids?.includes(grade.studentId),
  );
}

/**
 * Finds a row's current index by its stable id (see getRowId on the
 * gradebook's useReactTable config), so a focused/selected row can be
 * re-located after re-sorting shifts its position.
 *
 * Returns null when there's nothing to locate yet: no id being tracked, or
 * tableRows is momentarily empty (e.g. a page still loading) - the caller
 * should leave the current focus/highlight state alone in that case, not
 * treat it as the row having disappeared.
 *
 * Otherwise returns the row's index, or -1 if rowId isn't present in
 * tableRows (e.g. the student was paged or filtered away).
 *
 * @param {Array<{ id: string }>} tableRows
 * @param {string|null} rowId
 * @returns {number|null}
 */
export function findRowIndexById(tableRows, rowId) {
  if (!rowId || tableRows.length === 0) return null;
  return tableRows.findIndex((row) => row.id === rowId);
}

export { precisionFrom, GRADEBOOK_SETTINGS };
